'use client';

/**
 * Component Inspector
 * Panel for editing selected component properties
 */

import {
  SystemComponent,
  SystemConnection,
  ComponentType,
  ConnectionType,
  DataFlow,
  CloudProvider,
  COMPONENT_TYPE_CONFIG,
  CONNECTION_TYPE_CONFIG,
  DATA_FLOW_CONFIG,
  CLOUD_PRICING,
} from '@/lib/system-design/types';

interface ComponentInspectorProps {
  selectedComponent?: SystemComponent | null;
  selectedConnection?: SystemConnection | null;
  onUpdateComponent: (component: SystemComponent) => void;
  onUpdateConnection: (connection: SystemConnection) => void;
  onDeleteComponent: (id: string) => void;
  onDeleteConnection: (id: string) => void;
  canEdit?: boolean;
  canEstimateCosts?: boolean;
}

export function ComponentInspector({
  selectedComponent,
  selectedConnection,
  onUpdateComponent,
  onUpdateConnection,
  onDeleteComponent,
  onDeleteConnection,
  canEdit = true,
  canEstimateCosts = false,
}: ComponentInspectorProps) {
  if (!selectedComponent && !selectedConnection) {
    return (
      <div className="w-72 bg-background border-l border-border p-4">
        <p className="text-foreground-tertiary text-center py-8">
          Select a component or connection to view properties
        </p>
      </div>
    );
  }

  // Component inspector
  if (selectedComponent) {
    const config = COMPONENT_TYPE_CONFIG[selectedComponent.type];

    return (
      <div className="w-72 bg-background border-l border-border flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
              style={{ backgroundColor: config.bgColor }}
            >
              {config.icon}
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{config.label}</h3>
              <p className="text-xs text-foreground-tertiary">{config.description}</p>
            </div>
          </div>
        </div>

        {/* Properties */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Name</label>
            <input
              type="text"
              value={selectedComponent.name}
              onChange={(e) =>
                onUpdateComponent({ ...selectedComponent, name: e.target.value })
              }
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Type</label>
            <select
              value={selectedComponent.type}
              onChange={(e) =>
                onUpdateComponent({
                  ...selectedComponent,
                  type: e.target.value as ComponentType,
                })
              }
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            >
              {(Object.keys(COMPONENT_TYPE_CONFIG) as ComponentType[]).map((type) => (
                <option key={type} value={type}>
                  {COMPONENT_TYPE_CONFIG[type].label}
                </option>
              ))}
            </select>
          </div>

          {/* Technology */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Technology</label>
            <select
              value={selectedComponent.technology || ''}
              onChange={(e) =>
                onUpdateComponent({
                  ...selectedComponent,
                  technology: e.target.value || undefined,
                })
              }
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            >
              <option value="">Select technology...</option>
              {config.technologies.map((tech) => (
                <option key={tech} value={tech}>
                  {tech}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Description</label>
            <textarea
              value={selectedComponent.description || ''}
              onChange={(e) =>
                onUpdateComponent({
                  ...selectedComponent,
                  description: e.target.value || undefined,
                })
              }
              disabled={!canEdit}
              rows={2}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50 resize-none"
            />
          </div>

          {/* Scaling */}
          <div className="border-t border-border-muted pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Scaling</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Replicas</label>
                <input
                  type="number"
                  min={1}
                  value={selectedComponent.scaling?.replicas || 1}
                  onChange={(e) =>
                    onUpdateComponent({
                      ...selectedComponent,
                      scaling: {
                        ...selectedComponent.scaling,
                        replicas: parseInt(e.target.value) || 1,
                      },
                    })
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Strategy</label>
                <select
                  value={selectedComponent.scaling?.strategy || ''}
                  onChange={(e) =>
                    onUpdateComponent({
                      ...selectedComponent,
                      scaling: {
                        ...selectedComponent.scaling,
                        strategy: e.target.value as 'horizontal' | 'vertical' | 'auto' || undefined,
                      },
                    })
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                >
                  <option value="">None</option>
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertical</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
            </div>
          </div>

          {/* Cost estimation */}
          {canEstimateCosts && (
            <div className="border-t border-border-muted pt-4">
              <h4 className="text-sm font-medium text-foreground mb-3">Cost Estimation</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-foreground-tertiary mb-1">Provider</label>
                  <select
                    value={selectedComponent.cost?.provider || 'generic'}
                    onChange={(e) => {
                      const provider = e.target.value as CloudProvider;
                      const baseCost = CLOUD_PRICING[provider][selectedComponent.type].baseCost;
                      onUpdateComponent({
                        ...selectedComponent,
                        cost: {
                          provider,
                          monthlyCost: baseCost,
                        },
                      });
                    }}
                    disabled={!canEdit}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                  >
                    <option value="generic">Generic</option>
                    <option value="aws">AWS</option>
                    <option value="gcp">GCP</option>
                    <option value="azure">Azure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-foreground-tertiary mb-1">Monthly Cost ($)</label>
                  <input
                    type="number"
                    min={0}
                    value={selectedComponent.cost?.monthlyCost || 0}
                    onChange={(e) =>
                      onUpdateComponent({
                        ...selectedComponent,
                        cost: {
                          ...selectedComponent.cost,
                          provider: selectedComponent.cost?.provider || 'generic',
                          monthlyCost: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    disabled={!canEdit}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                  />
                </div>
                {selectedComponent.scaling?.replicas && selectedComponent.scaling.replicas > 1 && selectedComponent.cost?.monthlyCost && (
                  <p className="text-xs text-foreground-tertiary">
                    Total: ${(selectedComponent.cost.monthlyCost * selectedComponent.scaling.replicas).toFixed(2)}/mo
                    ({selectedComponent.scaling.replicas} replicas)
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Position */}
          <div className="border-t border-border-muted pt-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Position</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">X</label>
                <input
                  type="number"
                  value={Math.round(selectedComponent.position.x)}
                  onChange={(e) =>
                    onUpdateComponent({
                      ...selectedComponent,
                      position: {
                        ...selectedComponent.position,
                        x: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Y</label>
                <input
                  type="number"
                  value={Math.round(selectedComponent.position.y)}
                  onChange={(e) =>
                    onUpdateComponent({
                      ...selectedComponent,
                      position: {
                        ...selectedComponent.position,
                        y: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="p-4 border-t border-border">
            <button
              onClick={() => onDeleteComponent(selectedComponent.id)}
              className="w-full px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
            >
              Delete Component
            </button>
          </div>
        )}
      </div>
    );
  }

  // Connection inspector
  if (selectedConnection) {
    const connectionConfig = CONNECTION_TYPE_CONFIG[selectedConnection.connectionType];

    return (
      <div className="w-72 bg-background border-l border-border flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Connection</h3>
          <p className="text-xs text-foreground-tertiary">{connectionConfig.description}</p>
        </div>

        {/* Properties */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Connection Type</label>
            <select
              value={selectedConnection.connectionType}
              onChange={(e) =>
                onUpdateConnection({
                  ...selectedConnection,
                  connectionType: e.target.value as ConnectionType,
                })
              }
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            >
              {(Object.keys(CONNECTION_TYPE_CONFIG) as ConnectionType[]).map((type) => (
                <option key={type} value={type}>
                  {CONNECTION_TYPE_CONFIG[type].label}
                </option>
              ))}
            </select>
          </div>

          {/* Data Flow */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Data Flow</label>
            <select
              value={selectedConnection.dataFlow}
              onChange={(e) =>
                onUpdateConnection({
                  ...selectedConnection,
                  dataFlow: e.target.value as DataFlow,
                })
              }
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            >
              {(Object.keys(DATA_FLOW_CONFIG) as DataFlow[]).map((flow) => (
                <option key={flow} value={flow}>
                  {DATA_FLOW_CONFIG[flow].label}
                </option>
              ))}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Label</label>
            <input
              type="text"
              value={selectedConnection.label || ''}
              onChange={(e) =>
                onUpdateConnection({
                  ...selectedConnection,
                  label: e.target.value || undefined,
                })
              }
              placeholder="e.g., REST, gRPC"
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            />
          </div>

          {/* Protocol */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Protocol</label>
            <input
              type="text"
              value={selectedConnection.protocol || ''}
              onChange={(e) =>
                onUpdateConnection({
                  ...selectedConnection,
                  protocol: e.target.value || undefined,
                })
              }
              placeholder="e.g., HTTPS, TCP"
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            />
          </div>

          {/* Port */}
          <div>
            <label className="block text-sm text-foreground-secondary mb-1">Port</label>
            <input
              type="number"
              value={selectedConnection.port || ''}
              onChange={(e) =>
                onUpdateConnection({
                  ...selectedConnection,
                  port: parseInt(e.target.value) || undefined,
                })
              }
              placeholder="e.g., 443, 5432"
              disabled={!canEdit}
              className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
            />
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="p-4 border-t border-border">
            <button
              onClick={() => onDeleteConnection(selectedConnection.id)}
              className="w-full px-4 py-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
            >
              Delete Connection
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default ComponentInspector;
